import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Search, ChevronLeft, ChevronRight, Globe, Plus } from 'lucide-react';
import { fetchCountries, fetchCountriesCount } from '../services/api.ts';
import './Countries.css';

interface Country {
  id: number;
  name: string;
  code: string;
  enable: boolean;
}

const Countries: React.FC = () => {
  const navigate = useNavigate();
  const [countries, setCountries] = useState<Country[]>([]);
  const [totalCountries, setTotalCountries] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const countriesPerPage = 12;

  useEffect(() => {
    const loadCountries = async () => {
      try {
        setLoading(true);
        const offset = (currentPage - 1) * countriesPerPage;
        const [countriesData, countData] = await Promise.all([
          fetchCountries(offset, countriesPerPage),
          fetchCountriesCount()
        ]);
        setCountries(countriesData);
        setTotalCountries(countData);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load countries');
        console.error('Error loading countries:', err);
      } finally {
        setLoading(false);
      }
    };

    loadCountries();
  }, [currentPage]);

  const filteredCountries = countries.filter((country) => {
    const matchesSearch = 
      country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      country.code.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus =
      filterStatus === 'all' ||
      (filterStatus === 'active' && country.enable) ||
      (filterStatus === 'inactive' && !country.enable);

    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(totalCountries / countriesPerPage);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="countries-page">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading countries...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="countries-page">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="countries-page">
      <div className="page-header">
        <h2>Countries</h2>
        <div className="page-actions">
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search countries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="add-btn" onClick={() => navigate('/dashboard/countries/new')}>
            <Plus size={18} />
            Add New Country
          </button>
        </div>
      </div>

      <div className="filter-section">
        <div className="filter-group">
          <label>Status:</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as any)}>
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Showing:</label>
          <span style={{ color: '#6366f1', fontWeight: 600 }}>
            {filteredCountries.length} of {totalCountries} countries
          </span>
        </div>
      </div>

      {filteredCountries.length === 0 ? (
        <div className="empty-state">
          <p>No countries found matching your criteria.</p>
        </div>
      ) : (
        <>
          <div className="countries-table-container">
            <table className="countries-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCountries.map((country) => (
                  <tr key={country.id}>
                    <td className="cell-id">{country.id}</td>
                    <td className="cell-name">
                      <Globe size={16} />
                      {country.name}
                    </td>
                    <td className="cell-code">{country.code}</td>
                    <td>
                      <span className={`status-badge ${country.enable ? 'active' : 'inactive'}`}>
                        {country.enable ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="cell-actions">
                      <button
                        className="edit-btn"
                        onClick={() => navigate(`/dashboard/countries/edit/${country.id}`)}
                        title="Edit"
                      >
                        <Edit size={16} />
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={18} />
                Previous
              </button>

              <div className="pagination-info">
                Page {currentPage} of {totalPages}
              </div>

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Countries;
